import { Head, Link, router, usePage } from "@inertiajs/react";
import { AlertTriangle, Check, CheckCircle2, Copy, Link2, XCircle } from "lucide-react";
import type * as React from "react";
import { useEffect, useState } from "react";

import { ActivationGuide } from "@/components/activation-guide";
import { FinalCta } from "@/components/final-cta";
import MetaTagAnalysis from "@/components/meta-tag-analysis";
import { OpenGraphDebuggerGuide } from "@/components/open-graph-debugger-guide";
import DiscordPreview from "@/components/previews/DiscordPreview";
import FacebookPreview from "@/components/previews/FacebookPreview";
import LinkedInPreview from "@/components/previews/LinkedInPreview";
import SlackPreview from "@/components/previews/SlackPreview";
import TwitterPreview from "@/components/previews/TwitterPreview";
import WhatsAppPreview from "@/components/previews/WhatsAppPreview";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface MetaTags {
  og_title?: string;
  og_type?: string;
  og_image?: string;
  og_image_width?: string;
  og_image_height?: string;
  og_url?: string;
  og_description?: string;
  og_site_name?: string;
  twitter_card?: string;
  twitter_site?: string;
  twitter_title?: string;
  twitter_description?: string;
  twitter_image?: string;
  twitter_image_alt?: string;
}

interface ImageDetails {
  url?: string;
  content_type?: string;
  file_size?: number;
  file_size_kb?: number;
  file_size_mb?: number;
  width?: number;
  height?: number;
  aspect_ratio?: number;
}

interface Issue {
  type: "warning" | "error";
  category: string;
  message: string;
}

interface DebuggerFaq {
  question: string;
  answer: string;
}

interface PageProps {
  url?: string;
  page_title?: string;
  faqs?: DebuggerFaq[];
  meta_tags?: MetaTags;
  image_details?: ImageDetails;
  image_issues?: Issue[];
  text_issues?: Issue[];
  error?: string;
}

const RECENT_URLS_KEY = "og-debugger-recent-urls";
const EXAMPLE_URLS = ["github.com", "stripe.com", "theverge.com"];

const PLATFORM_TABS = [
  { value: "twitter", label: "X (Twitter)" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "slack", label: "Slack" },
  { value: "discord", label: "Discord" },
  { value: "whatsapp", label: "WhatsApp" },
];

function computeScore(metaTags: MetaTags, issues: Issue[]): number {
  let score = 100;
  if (!metaTags.og_image && !metaTags.twitter_image) score -= 40;
  for (const issue of issues) {
    score -= issue.type === "error" ? 15 : 5;
  }
  if (!metaTags.twitter_card) score -= 10;
  if (!metaTags.og_url) score -= 5;
  if (!metaTags.og_type) score -= 5;
  return Math.max(0, score);
}

function scoreLabel(score: number): { label: string; className: string } {
  if (score >= 90)
    return { label: "Excellent", className: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" };
  if (score >= 70)
    return { label: "Good", className: "bg-lime-100 text-lime-800 dark:bg-lime-900/40 dark:text-lime-300" };
  if (score >= 50)
    return {
      label: "Needs work",
      className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
    };
  return { label: "Poor", className: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" };
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildMetaSnippet(metaTags: MetaTags, pageUrl?: string): string {
  const val = (value: string | undefined, placeholder: string) =>
    value?.trim() ? escapeAttribute(value.trim()) : placeholder;

  const lines = [
    `<meta property="og:title" content="${val(metaTags.og_title, "Your page title (40-70 characters)")}" />`,
    `<meta property="og:description" content="${val(metaTags.og_description, "A short, compelling summary (100-160 characters)")}" />`,
    `<meta property="og:type" content="${val(metaTags.og_type, "website")}" />`,
    `<meta property="og:url" content="${val(metaTags.og_url ?? pageUrl, "https://example.com/page")}" />`,
    `<meta property="og:image" content="${val(metaTags.og_image, "https://example.com/og-image.png")}" />`,
    metaTags.og_site_name
      ? `<meta property="og:site_name" content="${escapeAttribute(metaTags.og_site_name)}" />`
      : null,
    `<meta name="twitter:card" content="${val(metaTags.twitter_card, "summary_large_image")}" />`,
    `<meta name="twitter:title" content="${val(metaTags.twitter_title ?? metaTags.og_title, "Your page title")}" />`,
    `<meta name="twitter:description" content="${val(metaTags.twitter_description ?? metaTags.og_description, "A short, compelling summary")}" />`,
    `<meta name="twitter:image" content="${val(metaTags.twitter_image ?? metaTags.og_image, "https://example.com/og-image.png")}" />`,
    `<meta name="twitter:image:alt" content="${val(metaTags.twitter_image_alt, "Describe the image for screen readers")}" />`,
  ];

  return lines.filter(Boolean).join("\n");
}

function LengthMeter({
  label,
  value,
  min,
  max,
  scale,
}: {
  label: string;
  value?: string;
  min: number;
  max: number;
  scale: number;
}) {
  const length = value?.trim().length ?? 0;
  const fillPercent = Math.min(100, (length / scale) * 100);
  const inRange = length >= min && length <= max;
  const fillColor = length === 0 ? "bg-red-400" : inRange ? "bg-green-500" : "bg-yellow-500";

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-muted-foreground text-xs">
          {length === 0 ? "missing" : `${length} chars`}{" "}
          <span className="hidden sm:inline">
            (aim for {min}-{max})
          </span>
        </span>
      </div>
      <div className="relative mt-1 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="absolute h-full bg-green-500/20"
          style={{ left: `${(min / scale) * 100}%`, width: `${((max - min) / scale) * 100}%` }}
        />
        <div className={`absolute h-full rounded-full ${fillColor}`} style={{ width: `${fillPercent}%` }} />
      </div>
    </div>
  );
}

function SpecRow({ label, value, status }: { label: string; value: string; status: "good" | "warn" | "bad" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-600 dark:text-gray-400">{label}:</span>
      <div className="flex items-center gap-2">
        <span className="font-mono whitespace-nowrap">{value}</span>
        {status === "good" && <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />}
        {status === "warn" && <AlertTriangle className="h-4 w-4 flex-shrink-0 text-yellow-600" />}
        {status === "bad" && <XCircle className="h-4 w-4 flex-shrink-0 text-red-600" />}
      </div>
    </div>
  );
}

function IssueRow({ issue }: { issue: Issue }) {
  return (
    <div
      className={`flex items-start gap-3 rounded-md p-3 ${
        issue.type === "error"
          ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          : "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
      }`}
    >
      <span className="mt-0.5">
        {issue.type === "error" ? <XCircle className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
      </span>
      <span className="text-sm">{issue.message}</span>
    </div>
  );
}

export default function Debugger() {
  const {
    url: initialUrl,
    page_title: pageTitle,
    faqs,
    meta_tags,
    image_details,
    image_issues,
    text_issues,
    error,
  } = usePage<PageProps & Record<string, unknown>>().props;
  const generateHref = initialUrl ? `/new?url=${encodeURIComponent(initialUrl)}` : "/new";

  const [url, setUrl] = useState(initialUrl ?? "");
  const [loading, setLoading] = useState(false);
  const [recentUrls, setRecentUrls] = useState<string[]>([]);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(RECENT_URLS_KEY) ?? "[]");
      if (Array.isArray(stored)) {
        setRecentUrls(stored.filter((entry): entry is string => typeof entry === "string").slice(0, 5));
      }
    } catch {
      // Corrupt storage, ignore
    }
  }, []);

  const submitUrl = (raw: string) => {
    let processed = raw.trim();
    if (!processed) return;
    if (!/^https?:\/\//i.test(processed)) {
      processed = "https://" + processed;
    }
    setUrl(processed);
    setRecentUrls((prev) => {
      const next = [processed, ...prev.filter((entry) => entry !== processed)].slice(0, 5);
      try {
        localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable, ignore
      }
      return next;
    });
    setLoading(true);
    router.get(
      "/open-graph-debugger",
      { url: processed },
      {
        preserveState: true,
        onFinish: () => setLoading(false),
      },
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitUrl(url);
  };

  const handleCopySnippet = async () => {
    try {
      await navigator.clipboard.writeText(buildMetaSnippet(meta_tags ?? {}, initialUrl));
      setCopiedSnippet(true);
      setTimeout(() => setCopiedSnippet(false), 1500);
    } catch {
      // Clipboard unavailable, ignore
    }
  };

  const handleCopyReportLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    } catch {
      // Clipboard unavailable, ignore
    }
  };

  const allIssues = [...(image_issues ?? []), ...(text_issues ?? [])];
  const errorCount = allIssues.filter((issue) => issue.type === "error").length;
  const warningCount = allIssues.filter((issue) => issue.type === "warning").length;
  const score = meta_tags ? computeScore(meta_tags, allIssues) : 0;
  const grade = scoreLabel(score);
  const suggestionUrls = recentUrls.length > 0 ? recentUrls : EXAMPLE_URLS;

  return (
    <>
      <Head title={pageTitle ?? "Open Graph Debugger & Checker | Free OG Preview"} />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        <div className="container mx-auto max-w-6xl px-4 py-8 md:py-12">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
              Open Graph Checker &amp; Debugger
            </h1>
            <p className="text-muted-foreground mt-4 max-w-2xl text-base md:text-lg">
              Free open graph debugger and checker. Paste a URL to preview link cards on Facebook, X, LinkedIn, Slack,
              Discord, and WhatsApp, then fix a weak image.
            </p>
          </div>

          <div className="mt-8 md:mt-12">
            <form onSubmit={handleSubmit} noValidate>
              <div className="mx-auto max-w-2xl">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    autoFocus
                    disabled={loading}
                    className="h-12 text-base"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={loading || !url.trim()}
                    className="h-12 min-w-[140px] bg-orange-500 whitespace-nowrap hover:bg-orange-600"
                  >
                    {loading ? "Checking..." : "Check URL"}
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                  <span className="text-muted-foreground text-xs">{recentUrls.length > 0 ? "Recent:" : "Try:"}</span>
                  {suggestionUrls.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => submitUrl(suggestion)}
                      disabled={loading}
                      className="text-muted-foreground hover:text-foreground max-w-[16rem] truncate rounded-full border bg-white px-3 py-1 text-xs transition hover:border-orange-300 disabled:opacity-50 dark:bg-gray-800"
                    >
                      {suggestion.replace(/^https?:\/\//i, "")}
                    </button>
                  ))}
                </div>
              </div>
            </form>
          </div>

          <ActivationGuide
            title="Fix the card after you check it"
            links={[
              {
                href: "/new",
                label: "Generate an OG image",
                detail: "Build a 1200×630 replacement for the URL you just checked.",
              },
              {
                href: "/docs",
                label: "Use the API",
                detail: "Return that image from your app instead of uploading a file by hand.",
              },
              {
                href: "/docs/mcp",
                label: "Use MCP",
                detail: "Ask an agent to generate the preview from the same public URL.",
              },
              {
                href: "/wordpress",
                label: "WordPress plugin",
                detail: "Write og:image and Twitter cards for posts, pages, and WooCommerce.",
              },
              {
                href: "/change-og-image-wordpress",
                label: "Change the WordPress OG image",
                detail: "Featured image, SEO-plugin social image, or a generated card.",
              },
              {
                href: "/open-graph-protocol-wordpress",
                label: "Open Graph protocol on WordPress",
                detail: "What og:title, og:description, og:image, and og:url mean in a theme or plugin.",
              },
            ]}
          >
            <p>
              This checker reads og:title, og:description, og:image, and the Twitter card tags, then previews the unfurl
              on Facebook, X, LinkedIn, Slack, Discord, and WhatsApp. A missing or tiny image is the usual reason a link
              looks unfinished. Generate a replacement, or publish the same image with the API or MCP, then run the URL
              again. The guide below compares Meta, X, LinkedIn, WhatsApp, Messages, and Slack, including the cases
              their own tools do not catch. Carrd sites upload the file under Publish → Media. The{" "}
              <Link
                href="/carrd-social-image"
                className="font-medium text-orange-700 hover:underline dark:text-orange-300"
              >
                Carrd social image guide
              </Link>{" "}
              walks through that step.
            </p>
          </ActivationGuide>

          {error && (
            <div className="mx-auto mt-8 max-w-4xl md:mt-12">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/50 dark:text-red-300">
                <p className="font-semibold">Error</p>
                <p className="mt-1">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={loading}
                  onClick={() => submitUrl(initialUrl ?? url)}
                >
                  Try again
                </Button>
              </div>
            </div>
          )}

          {loading && !meta_tags && !error && (
            <div className="mx-auto mt-12 w-full max-w-lg space-y-4">
              <Skeleton className="aspect-[1.91/1] w-full rounded-2xl" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {meta_tags && (
            <div className={`mt-12 md:mt-16 ${loading ? "pointer-events-none opacity-60 transition-opacity" : ""}`}>
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
                  Your Social Share Previews
                </h2>
                <p className="text-muted-foreground mt-2">
                  See how your page will appear when shared on different platforms
                </p>
              </div>

              {/* Score summary */}
              <div className="mx-auto mb-6 max-w-4xl">
                <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg border bg-white px-4 py-3 shadow-sm dark:bg-gray-800">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${grade.className}`}
                  >
                    {score}/100 · {grade.label}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {allIssues.length === 0
                      ? "No issues found"
                      : [
                          errorCount > 0 ? `${errorCount} issue${errorCount > 1 ? "s" : ""}` : null,
                          warningCount > 0 ? `${warningCount} warning${warningCount > 1 ? "s" : ""}` : null,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                  </span>
                  {allIssues.length > 0 && (
                    <a href="#analysis" className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400">
                      View details ↓
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleCopyReportLink()}
                    className="text-muted-foreground hover:text-foreground ml-auto inline-flex items-center gap-1.5 text-sm transition"
                  >
                    {copiedLink ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
                    {copiedLink ? "Copied" : "Copy report link"}
                  </button>
                </div>
              </div>

              <div className="mx-auto mb-8 max-w-4xl rounded-lg border border-orange-200 bg-white p-5 text-left shadow-sm dark:border-orange-500/30 dark:bg-gray-800">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Generate a replacement image
                    </h3>
                    <p className="text-muted-foreground mt-1 text-sm">
                      Create a branded Open Graph image for this URL, or publish the same preview with the API or MCP.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button asChild className="bg-orange-500 whitespace-nowrap text-white hover:bg-orange-600">
                      <Link href={generateHref}>Generate image</Link>
                    </Button>
                    <Button asChild variant="outline" className="whitespace-nowrap">
                      <Link href="/docs">Use the API</Link>
                    </Button>
                    <Button asChild variant="outline" className="whitespace-nowrap">
                      <Link href="/docs/mcp">Use MCP</Link>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mb-8 md:mb-12">
                <Tabs defaultValue="twitter" className="w-full">
                  <div className="mb-6 flex justify-center">
                    <TabsList className="grid w-full max-w-2xl grid-cols-3 sm:grid-cols-6">
                      {PLATFORM_TABS.map((tab) => (
                        <TabsTrigger key={tab.value} value={tab.value} className="text-xs md:text-sm">
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  <TabsContent value="twitter" className="mt-6">
                    <div className="flex justify-center">
                      <TwitterPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                  <TabsContent value="facebook" className="mt-6">
                    <div className="flex justify-center">
                      <FacebookPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                  <TabsContent value="linkedin" className="mt-6">
                    <div className="flex justify-center">
                      <LinkedInPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                  <TabsContent value="slack" className="mt-6">
                    <div className="flex justify-center">
                      <SlackPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                  <TabsContent value="discord" className="mt-6">
                    <div className="flex justify-center">
                      <DiscordPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                  <TabsContent value="whatsapp" className="mt-6">
                    <div className="flex justify-center">
                      <WhatsAppPreview metaTags={meta_tags} />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-2">
                <MetaTagAnalysis
                  title="Open Graph Tags"
                  tags={[
                    {
                      name: "og:title",
                      value: meta_tags.og_title,
                      required: true,
                    },
                    {
                      name: "og:type",
                      value: meta_tags.og_type,
                      required: true,
                    },
                    {
                      name: "og:image",
                      value: meta_tags.og_image,
                      required: true,
                    },
                    { name: "og:url", value: meta_tags.og_url, required: true },
                    { name: "og:description", value: meta_tags.og_description },
                    { name: "og:site_name", value: meta_tags.og_site_name },
                  ]}
                />
                <MetaTagAnalysis
                  title="X (Twitter) Card Tags"
                  tags={[
                    {
                      name: "twitter:title",
                      value: meta_tags.twitter_title,
                      required: true,
                    },
                    {
                      name: "twitter:card",
                      value: meta_tags.twitter_card,
                      required: true,
                    },
                    {
                      name: "twitter:image",
                      value: meta_tags.twitter_image,
                      required: true,
                    },
                    {
                      name: "twitter:image:alt",
                      value: meta_tags.twitter_image_alt,
                    },
                    {
                      name: "twitter:description",
                      value: meta_tags.twitter_description,
                    },
                    { name: "twitter:site", value: meta_tags.twitter_site },
                  ]}
                />
              </div>

              {/* OG Tags Analysis Section */}
              <div id="analysis" className="mx-auto mt-12 max-w-4xl scroll-mt-20 md:mt-16">
                <div className="rounded-lg border bg-white p-6 shadow-sm dark:bg-gray-800">
                  <h3 className="mb-4 text-lg font-semibold">OG Tags Analysis</h3>

                  {/* Text length meters */}
                  <div className="mb-6 space-y-3">
                    <LengthMeter label="og:title" value={meta_tags.og_title} min={40} max={70} scale={100} />
                    <LengthMeter
                      label="og:description"
                      value={meta_tags.og_description}
                      min={100}
                      max={160}
                      scale={220}
                    />
                  </div>

                  {allIssues.length === 0 && (meta_tags.og_image || meta_tags.twitter_image) && (
                    <div className="mb-6 flex items-center gap-3 rounded-md bg-green-50 p-4 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm font-medium">All specifications meet optimal standards!</span>
                    </div>
                  )}

                  {allIssues.length > 0 && (
                    <div className="mb-6 space-y-3">
                      {allIssues.map((issue, index) => (
                        <IssueRow key={`${issue.category}-${index}`} issue={issue} />
                      ))}
                    </div>
                  )}

                  {/* Image Specifications */}
                  {image_details && Object.keys(image_details).length > 0 && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <h4 className="mb-2 font-medium text-gray-700 dark:text-gray-300">Dimensions</h4>
                        <div className="space-y-2 text-sm">
                          <SpecRow
                            label="Size"
                            value={
                              image_details.width && image_details.height
                                ? `${image_details.width}×${image_details.height}px`
                                : meta_tags.og_image_width && meta_tags.og_image_height
                                  ? `${meta_tags.og_image_width}×${meta_tags.og_image_height}px`
                                  : "Not specified"
                            }
                            status={image_details.width === 1200 && image_details.height === 630 ? "good" : "warn"}
                          />
                          <SpecRow
                            label="Aspect Ratio"
                            value={
                              image_details.aspect_ratio
                                ? image_details.aspect_ratio === 1
                                  ? "1:1"
                                  : `${image_details.aspect_ratio}:1`
                                : "Not calculated"
                            }
                            status={
                              image_details.aspect_ratio && Math.abs(image_details.aspect_ratio - 1.91) < 0.1
                                ? "good"
                                : "warn"
                            }
                          />
                          <div className="text-xs text-gray-500 dark:text-gray-500">
                            Recommended: 1200×630px (1.91:1)
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium text-gray-700 dark:text-gray-300">File Info</h4>
                        <div className="space-y-2 text-sm">
                          <SpecRow
                            label="File Size"
                            value={
                              image_details.file_size_kb && image_details.file_size_kb < 1024
                                ? `${image_details.file_size_kb}KB`
                                : image_details.file_size_mb
                                  ? `${image_details.file_size_mb}MB`
                                  : "Unknown"
                            }
                            status={image_details.file_size_mb && image_details.file_size_mb <= 1 ? "good" : "bad"}
                          />
                          <SpecRow
                            label="Format"
                            value={
                              image_details.content_type
                                ? image_details.content_type.replace("image/", "").toUpperCase()
                                : "Unknown"
                            }
                            status={
                              image_details.content_type &&
                              (image_details.content_type.includes("jpeg") ||
                                image_details.content_type.includes("jpg") ||
                                image_details.content_type.includes("png"))
                                ? "good"
                                : "warn"
                            }
                          />
                          <div className="text-xs text-gray-500 dark:text-gray-500">Recommended: JPEG/PNG, &lt;1MB</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Optimal Specs Reference */}
                  <div className="mt-6 rounded-md bg-gray-50 p-4 dark:bg-gray-700/50">
                    <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Optimal Specifications
                    </h4>
                    <div className="mb-3">
                      <h5 className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">Text Content</h5>
                      <div className="grid gap-1 text-xs text-gray-600 md:grid-cols-2 dark:text-gray-400">
                        <div>• Title: 60-70 characters</div>
                        <div>• Description: 150-160 characters</div>
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">Image</h5>
                      <div className="grid gap-1 text-xs text-gray-600 md:grid-cols-2 dark:text-gray-400">
                        <div>• Size: 1200×630px</div>
                        <div>• Aspect Ratio: 1.91:1</div>
                        <div>• Format: JPEG (photos) or PNG (graphics)</div>
                        <div>• File Size: 200-500KB (JPEG), 500KB-1MB (PNG)</div>
                        <div>• Resolution: 72 DPI</div>
                        <div>• Maximum: &lt;1MB for optimal loading</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Copy-paste fix */}
                <div className="mt-6 rounded-lg border bg-white p-6 shadow-sm dark:bg-gray-800">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold">Recommended meta tags</h3>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Ready to paste into your <code>&lt;head&gt;</code>. Values we found are filled in, missing ones
                        have placeholders.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCopySnippet()}
                      className="flex-shrink-0"
                    >
                      {copiedSnippet ? (
                        <Check className="mr-1.5 h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="mr-1.5 h-4 w-4" />
                      )}
                      {copiedSnippet ? "Copied" : "Copy"}
                    </Button>
                  </div>
                  <pre className="mt-4 overflow-x-auto rounded-md bg-gray-50 p-4 text-xs leading-5 dark:bg-gray-900/60">
                    <code>{buildMetaSnippet(meta_tags, initialUrl)}</code>
                  </pre>
                </div>
              </div>
            </div>
          )}

          <OpenGraphDebuggerGuide faqs={faqs} />
        </div>

        <FinalCta />
      </div>
      <SiteFooter />
    </>
  );
}
